import { type ChangeEvent, type ComponentProps, Fragment, useMemo, useState } from 'react';
import {
  type GqlMeetingParticipantsQuery,
  useDeleteMeetingParticipantMutation,
  useDeleteMeetingParticipantShareMutation,
  useMeetingParticipantsQuery,
  useMpSetPresentMutation,
  useMpSetProxyMutation,
} from '../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { orderBy } from 'lodash-es';
import { Trans, useLingui } from '@lingui/react';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { JuridicalKind } from '../../../models/identity.model';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { InputState } from '@startuptools/business-ui/identity/types';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { AlertDialog } from '../../../components/simple-dialogs/AlertDialog';
import { AddVotingListShare } from './AddVotingListShare';
import { EditVotingListShare } from './EditVotingListShare';
import { type ArrayElement } from '@startuptools/common/common';
import { ParticipantsSummonsDocument } from '../../meetings/components/edit-steps/ParticipantsSummons.graphql';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { useTheme } from '@startuptools/ui/Theme';
import { Button } from '@startuptools/ui/Button';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@startuptools/ui/IconButton';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Typography } from '@startuptools/ui/Typography';
import TablePagination from '@mui/material/TablePagination';
import { MeetingParticipantsTableInnerDocument } from '../../meetings/components/MeetingParticipantsTable.graphql';
import { PowerOfAttorneysDocument } from './edit-steps/PowerOfAttorneys.graphql';
import { ShareholdersMeetingDocument } from '../ShareholdersMeeting.graphql';
import { GqlCreateIdentityInput } from '../../../graphql/base-types.graphql';

export const VotingListTable = ({ meetingId }: { meetingId: string }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const theme = useTheme();
  const { data, loading, error, refetch } = useMeetingParticipantsQuery({
    variables: { companyId, meetingId, filter: { isShareholder: true } },
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteMpShare] = useDeleteMeetingParticipantShareMutation({ refetchQueries: [ShareholdersMeetingDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteMp] = useDeleteMeetingParticipantMutation({
    refetchQueries: [
      ShareholdersMeetingDocument,
      ParticipantsSummonsDocument,
      MeetingParticipantsTableInnerDocument,
      PowerOfAttorneysDocument,
    ],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetProxy] = useMpSetProxyMutation({
    refetchQueries: [ShareholdersMeetingDocument, ParticipantsSummonsDocument, PowerOfAttorneysDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetPresent] = useMpSetPresentMutation({
    refetchQueries: [ShareholdersMeetingDocument, ParticipantsSummonsDocument],
  });

  const [proxyMeetingParticipant, setProxyMeetingParticipant] = useState<ArrayElement<
    GqlMeetingParticipantsQuery['meetingParticipants']['models']
  > | null>(null);
  const [deleteDialogProps, setDeleteDialogProps] = useState<Pick<
    ComponentProps<typeof AlertDialog>,
    'onAccept'
  > | null>(null);
  const [addDialogProps, setAddDialogProps] = useState<Pick<
    ComponentProps<typeof AddVotingListShare>,
    'meetingParticipantId'
  > | null>(null);
  const [editDialogProps, setEditDialogProps] = useState<Pick<
    ComponentProps<typeof EditVotingListShare>,
    'meetingParticipantId' | 'meetingParticipantShareId'
  > | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const mpShareholders = useMemo(() => data?.meetingParticipants.models.filter(mp => mp.isShareholder), [data]);
  const meetingParticipants = useMemo(() => {
    if (!mpShareholders) {
      return [] as GqlMeetingParticipantsQuery['meetingParticipants']['models'];
    }
    const mps = orderBy(mpShareholders, mp => mp.identity.name, 'asc');
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return mps.slice(start, end);
  }, [mpShareholders, page, rowsPerPage]);

  if (error) {
    console.error(error);
    return null;
  }

  if (loading || !data?.meetingParticipants) {
    return null;
  }

  const handleSetRepresentative = async (representative: GqlCreateIdentityInput) => {
    if (!proxyMeetingParticipant) {
      throw new Error('proxyMeetingParticipantId was not set');
    }
    return doSetProxy({
      variables: { companyId, input: { id: proxyMeetingParticipant.id, proxyInput: identityToInput(representative) } },
    }).then(async () => {
      setProxyMeetingParticipant(null);
      return refetch();
    });
  };

  const handleCreate = (meetingParticipantId: string) => {
    setAddDialogProps({ meetingParticipantId });
  };

  const handleEdit = (meetingParticipantId: string, meetingParticipantShareId: string) => {
    setEditDialogProps({ meetingParticipantId, meetingParticipantShareId });
  };

  const handleDelete = (mpShareId: string) => {
    setDeleteDialogProps({
      onAccept: async () => {
        await doDeleteMpShare({ variables: { companyId, id: mpShareId } });
        await refetch();
        setDeleteDialogProps(null);
      },
    });
  };

  const handleDeleteMp = (mpId: string) => {
    setDeleteDialogProps({
      onAccept: async () => {
        await doDeleteMp({ variables: { companyId, id: mpId } });
        await refetch();
        setDeleteDialogProps(null);
      },
    });
  };

  const handleAttendToggle = (id: string, event: ChangeEvent<HTMLInputElement>) => {
    const present = event.target.checked;
    void doSetPresent({ variables: { companyId, input: { id, present } } });
    return;
  };

  return (
    <>
      <Stack gap={2}>
        {/* <TextField label={<Trans id="Filter" />} fullWidth></TextField> */}
        <Table sx={{ tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th>
                <Trans id="Att." />
              </th>
              <th>
                <Trans id="Participant" />
              </th>
              <th>
                <Trans id="Representative" />
              </th>
              <th style={{ backgroundColor: theme.palette.neutral[100] }} align="right">
                <Trans id="Share class" />
              </th>
              <th style={{ backgroundColor: theme.palette.neutral[100] }} align="right">
                <Trans id="Number of shares" />
              </th>
              <th style={{ backgroundColor: theme.palette.neutral[100] }} align="right">
                <Trans id="Number of Votes" />
              </th>
              <th style={{ backgroundColor: theme.palette.neutral[100] }}></th>
            </tr>
          </thead>
          <tbody>
            {meetingParticipants
              .filter(e => e.meetingParticipantShares.length === 0)
              .map(mp => {
                const Representative = () => {
                  if (!mp.present) return null;
                  const lackingProxy = mp.identity.juridicalKind === JuridicalKind.Company && !mp.proxyId;

                  if (mp.proxyId) {
                    return (
                      <Stack direction="row" gap={2}>
                        {mp.proxy && <Identity identity={mp.proxy} />}
                        <Tooltip title={<Trans id="Change representative" />}>
                          <IconButton onClick={() => setProxyMeetingParticipant(mp)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    );
                  }
                  return lackingProxy ? (
                    <Button
                      type="button"
                      variant="outlined"
                      startDecorator={<PriorityHighOutlinedIcon />}
                      color="danger"
                      onClick={() => setProxyMeetingParticipant(mp)}
                      size="md"
                    >
                      <Trans id="Add representative" />
                    </Button>
                  ) : (
                    <IconButton onClick={() => setProxyMeetingParticipant(mp)} variant="outlined" size="sm">
                      <EditIcon />
                    </IconButton>
                  );
                };

                return (
                  <tr key={mp.id}>
                    <td style={{ verticalAlign: 'top' }}>
                      <Checkbox
                        checked={mp.present}
                        onChange={event => handleAttendToggle(mp.id, event)}
                        sx={{ marginTop: 1 }}
                      />
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      <Identity identity={mp.identity} />
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      <Representative />
                    </td>
                    <td colSpan={3} style={{ backgroundColor: theme.palette.neutral[100] }} align="right">
                      <Stack direction="row" gap={2} alignItems="center" justifyContent="end">
                        <Trans id="Missing information about shares and votes" />
                        <Button variant="outlined" onClick={() => handleCreate(mp.id)}>
                          <Trans id="Add shares and votes" />
                        </Button>
                      </Stack>
                    </td>
                    <td style={{ backgroundColor: theme.palette.neutral[100], whiteSpace: 'nowrap' }} align="right">
                      <Tooltip title={<Trans id="Remove shareholder from voting list" />}>
                        <IconButton type="button" color="warning" onClick={() => handleDeleteMp(mp.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            {meetingParticipants
              .filter(e => e.meetingParticipantShares?.length > 0)
              .flatMap(mp => {
                const Representative = () => {
                  if (!mp.present) return null;
                  const lackingProxy = mp.identity.juridicalKind === JuridicalKind.Company && !mp.proxyId;

                  if (mp.proxyId) {
                    return (
                      <Stack direction="row" gap={2}>
                        {mp.proxy && <Identity identity={mp.proxy} />}
                        <Tooltip title={<Trans id="Change representative" />}>
                          <IconButton onClick={() => setProxyMeetingParticipant(mp)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    );
                  }
                  return lackingProxy ? (
                    <Button
                      type="button"
                      variant="outlined"
                      startDecorator={<PriorityHighOutlinedIcon />}
                      color="danger"
                      onClick={() => setProxyMeetingParticipant(mp)}
                      size="md"
                    >
                      <Trans id="Add representative" />
                    </Button>
                  ) : (
                    <IconButton onClick={() => setProxyMeetingParticipant(mp)} variant="plain" size="sm">
                      <EditIcon />
                    </IconButton>
                  );
                };

                const rowSpan = mp.meetingParticipantShares.length + 1;
                return (
                  <Fragment key={mp.id}>
                    {mp.meetingParticipantShares.flatMap((mpShare, idx) => (
                      <tr key={mpShare.id}>
                        {idx === 0 && (
                          <>
                            <td rowSpan={rowSpan} style={{ verticalAlign: 'top' }}>
                              <Checkbox
                                checked={mp.present}
                                onChange={event => handleAttendToggle(mp.id, event)}
                                sx={{ marginTop: 1 }}
                              />
                            </td>
                            <td rowSpan={rowSpan} style={{ verticalAlign: 'top' }}>
                              {mp.identity && <Identity identity={mp.identity} />}
                            </td>
                            <td rowSpan={rowSpan} style={{ verticalAlign: 'top' }}>
                              <Representative />
                            </td>
                          </>
                        )}
                        <td style={{ backgroundColor: theme.palette.neutral[100] }} align="right">
                          {mpShare.shareClass?.name}
                        </td>
                        <td style={{ backgroundColor: theme.palette.neutral[100] }} align="right">
                          {mpShare.numberOfShares ? (
                            <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                              {mpShare.numberOfShares}
                            </NumberFormat>
                          ) : (
                            <Typography noWrap>
                              <Button
                                variant="outlined"
                                size="sm"
                                onClick={() =>
                                  setEditDialogProps({
                                    meetingParticipantId: mp.id,
                                    meetingParticipantShareId: mpShare.id,
                                  })
                                }
                              >
                                <Trans id="Set number of shares" />
                              </Button>
                            </Typography>
                          )}
                        </td>
                        <td style={{ backgroundColor: theme.palette.neutral[100] }} align="right">
                          {mpShare.numberOfVotes ? (
                            <NumberFormat locale={i18n.locale} minimumFractionDigits={0}>
                              {mpShare.numberOfVotes}
                            </NumberFormat>
                          ) : (
                            <Typography noWrap>
                              <Button
                                variant="outlined"
                                size="sm"
                                onClick={() =>
                                  setEditDialogProps({
                                    meetingParticipantId: mp.id,
                                    meetingParticipantShareId: mpShare.id,
                                  })
                                }
                              >
                                <Trans id="Set number of votes" />
                              </Button>
                            </Typography>
                          )}
                        </td>
                        <td style={{ backgroundColor: theme.palette.neutral[100], whiteSpace: 'nowrap' }} align="right">
                          <Tooltip title={<Trans id="Edit shares and votes" />}>
                            <IconButton
                              type="button"
                              color="primary"
                              onClick={() => handleEdit(mpShare.meetingParticipantId, mpShare.id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <IconButton type="button" color="warning" onClick={() => handleDelete(mpShare.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </td>
                      </tr>
                    ))}

                    <tr key={mp.id}>
                      <td colSpan={4} style={{ backgroundColor: theme.palette.neutral[100] }} align="right">
                        <Tooltip title={<Trans id="Add share class" />}>
                          <Button variant="outlined" onClick={() => handleCreate(mp.id)}>
                            <Trans id="Add share class entry" />
                          </Button>
                        </Tooltip>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
          </tbody>
          <tfoot>
            <tr>
              <TablePagination
                align="right"
                labelRowsPerPage={<Trans id="Rows per page:" />}
                colSpan={7}
                count={data.meetingParticipants?.totalCount ?? 0}
                page={page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={event => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
              />
            </tr>
          </tfoot>
        </Table>
      </Stack>

      {proxyMeetingParticipant && (
        <AddIdentity
          availableInputStates={{
            [InputState.System]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
            [InputState.CompanyBoard]: true,
          }}
          companyBoardOrgNumber={proxyMeetingParticipant?.identity.identityNumber}
          headline={<Trans id="Set representative" />}
          juridicalKinds={[JuridicalKind.Person]}
          handleCreate={identity => handleSetRepresentative(identity)}
          onClose={() => setProxyMeetingParticipant(null)}
          allowProtectedIdentity={false}
        />
      )}

      {deleteDialogProps && (
        <AlertDialog
          title={<Trans id="Deleting voting list entry" />}
          onCancel={() => setDeleteDialogProps(null)}
          onAcceptButtonColor="warning"
          {...deleteDialogProps}
        >
          <Alert color="danger">
            <Trans id="Are you sure you want to delete this voting list entry?" />
          </Alert>
        </AlertDialog>
      )}

      {addDialogProps && <AddVotingListShare onClose={() => setAddDialogProps(null)} {...addDialogProps} />}
      {editDialogProps && <EditVotingListShare onClose={() => setEditDialogProps(null)} {...editDialogProps} />}
    </>
  );
};
